/**
 * <VidyaFigure /> — top-level dispatcher for the `vidya-figure` fenced code
 * block emitted by the math-mode response_generator.
 *
 * Receives the raw fenced-block body (a JSON string the LLM wrote). Parses,
 * validates against Zod, and dispatches to:
 *   kind="geometry" → <GeometryRenderer/>  (rough.js)
 *   kind="plot"     → <PlotRenderer/>      (function-plot)
 *
 * Both children are lazy-loaded so that non-math chat sessions (the vast
 * majority of users) don't pay the bundle cost. Validation failures and
 * runtime renderer errors show a graceful fallback instead of crashing
 * the surrounding markdown render.
 */

import { lazy, Suspense } from 'react';
import { parseVidyaFigure } from './schemas';
import type { GeometrySpecT, PlotSpecT } from './schemas';

const GeometryRenderer = lazy(() => import('./GeometryRenderer'));
const PlotRenderer = lazy(() => import('./PlotRenderer'));

interface Props {
  /** Raw JSON body of the ```vidya-figure ... ``` code block. */
  body: string;
}

export function VidyaFigure({ body }: Props) {
  const parsed = parseVidyaFigure(body);

  if (!parsed.ok) {
    return <FigureError message={parsed.error} body={body} />;
  }

  const block = parsed.data;

  // Per-branch cast: Zod v4 + this tsconfig don't narrow the
  // discriminated union via the runtime tag through to JSX prop typing,
  // but the runtime validation upstream already guarantees the shape.
  return (
    <Suspense fallback={<FigureLoading />}>
      {block.kind === 'geometry' ? (
        <GeometryRenderer spec={block.spec as GeometrySpecT} />
      ) : (
        <PlotRenderer spec={block.spec as PlotSpecT} />
      )}
    </Suspense>
  );
}

function FigureLoading() {
  return (
    <div className="my-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
      Loading diagram…
    </div>
  );
}

function FigureError({ message, body }: { message: string; body: string }) {
  // Graceful fallback — surfaces the underlying issue in dev, hides the raw
  // JSON from the student in prod. Keeps the answer readable instead of
  // dumping a stack trace inline.
  const isDev = process.env.NODE_ENV !== 'production';
  return (
    <div className="my-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
      <div className="text-sm text-amber-800 dark:text-amber-200">
        Diagram could not render — ask Vidya to retry.
      </div>
      {isDev && (
        <details className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          <summary className="cursor-pointer">why (dev only)</summary>
          <div className="mt-1 font-mono whitespace-pre-wrap">{message}</div>
          <pre className="mt-2 max-h-48 overflow-auto rounded bg-amber-100 dark:bg-amber-900/40 p-2">
            {body}
          </pre>
        </details>
      )}
    </div>
  );
}

export default VidyaFigure;
